// Shared confirmation modals used wherever the admin acts on a user
// (the user-list 3-dot menu, the user-detail page header, etc.).
//
// EmailConfirmModal — heavy gate. Requires the admin to type the
// user's email exactly before Confirm enables. Use for actions that
// are externally visible to the user OR change billing state, e.g.
// suspend.
//
// PlainConfirmModal — lighter gate. Single confirm + cancel; no
// typing. Use for reversible / no-billing actions, e.g. unsuspend.
//
// InfoModal — read-only explainer (help/docs). Wider card, scrollable
// body, single Close action. Use for "how does this page work?" UX.
//
// FormModal — same wide card around caller-owned form content, with
// Cancel + a primary action (e.g. editing an API key's scopes).
//
// All portal to document.body so the page below can't clip them and
// share keyboard handling (Esc closes).

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export function EmailConfirmModal({
  open,
  title,
  body,
  expectedEmail,
  confirmLabel = 'Confirm',
  confirmStyle = 'danger',
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  expectedEmail: string;
  confirmLabel?: string;
  confirmStyle?: 'danger' | 'primary';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toLowerCase() === expectedEmail.toLowerCase();

  // Reset every open so a re-shown modal doesn't carry the old value.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div style={modalBackdrop} onClick={onCancel}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 className="serif" style={modalTitle}>{title}</h3>
        <div style={modalBody}>{body}</div>
        <label style={modalLabel} className="mono">
          Type the user&rsquo;s email to confirm
        </label>
        <input
          type="text"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={expectedEmail}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          style={modalInput}
        />
        <div style={modalActions}>
          <button type="button" onClick={onCancel} style={modalCancelBtn}>Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!matches}
            style={{
              ...(confirmStyle === 'danger' ? modalDangerBtn : modalConfirmBtn),
              opacity: matches ? 1 : 0.4,
              cursor: matches ? 'pointer' : 'not-allowed',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function PlainConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  confirmStyle = 'primary',
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  confirmStyle?: 'danger' | 'primary';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div style={modalBackdrop} onClick={onCancel}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 className="serif" style={modalTitle}>{title}</h3>
        <div style={{ ...modalBody, marginBottom: 18 }}>{body}</div>
        <div style={modalActions}>
          <button type="button" onClick={onCancel} style={modalCancelBtn}>Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            style={confirmStyle === 'danger' ? modalDangerBtn : modalConfirmBtn}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function InfoModal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div style={modalBackdrop} onClick={onClose}>
      <div style={infoCard} onClick={(e) => e.stopPropagation()}>
        <h3 className="serif" style={modalTitle}>{title}</h3>
        <div style={{ ...modalBody, marginBottom: 18 }}>{children}</div>
        <div style={modalActions}>
          <button type="button" onClick={onClose} style={modalConfirmBtn}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * FormModal — a wide, scrollable card around arbitrary form content, with
 * Cancel + a primary action. The caller owns the form state; Esc and a
 * backdrop click cancel unless `busy`.
 */
export function FormModal({
  open,
  title,
  children,
  confirmLabel = 'Save',
  confirmDisabled = false,
  busy = false,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  const disabled = confirmDisabled || busy;

  return createPortal(
    <div style={modalBackdrop} onClick={() => { if (!busy) onCancel(); }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={infoCard}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="serif" style={modalTitle}>{title}</h3>
        <div style={{ ...modalBody, marginBottom: 18 }}>{children}</div>
        <div style={modalActions}>
          <button type="button" onClick={onCancel} disabled={busy} style={modalCancelBtn}>Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            style={{
              ...modalConfirmBtn,
              opacity: disabled ? 0.4 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ───── styling shared across all modals ───────────────────────────────────

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(43, 29, 20, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: 16,
};

const modalCard: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 18,
  padding: 24,
  width: '100%',
  maxWidth: 460,
  // A short phone viewport (keyboard open) scrolls the card rather than
  // clipping its actions.
  maxHeight: '100%',
  overflowY: 'auto',
  boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
};

// InfoModal variant — wider for prose, capped height with scroll so
// long help content never pushes the Close action off-screen.
const infoCard: React.CSSProperties = {
  ...modalCard,
  maxWidth: 580,
  maxHeight: '82vh',
  overflowY: 'auto',
};

const modalTitle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 500,
  fontStyle: 'italic',
  margin: '0 0 10px',
};

const modalBody: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ink-2)',
  lineHeight: 1.5,
  margin: '0 0 16px',
};

const modalLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ink-3)',
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginBottom: 6,
};

const modalInput: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: 'var(--font-mono)',
  outline: 'none',
  color: 'var(--ink)',
  marginBottom: 18,
};

const modalActions: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 8,
};

const modalCancelBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-3)',
  border: '1px solid var(--line)',
  borderRadius: 100,
  padding: '8px 16px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const modalConfirmBtn: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '8px 18px',
  fontWeight: 600,
  fontSize: 13,
  fontFamily: 'var(--font-ui)',
};

// Danger style — same shape, deeper red. Used for suspend.
const modalDangerBtn: React.CSSProperties = {
  ...modalConfirmBtn,
  background: '#a8331f',
};
